import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "@/lib/router-compat";

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col">
      <div className="container mx-auto max-w-4xl px-4 py-8 flex-1">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6 text-foreground hover:text-foreground/80"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Terug
        </Button>

        <div className="bg-card rounded-lg p-6 md:p-8 text-foreground">
          <h1 className="text-3xl font-bold mb-6">Algemene Voorwaarden</h1>
          <p className="text-sm text-muted-foreground mb-8">Laatst bijgewerkt: 15 januari 2026</p>

          <div className="space-y-6 prose prose-sm max-w-none dark:prose-invert">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Definities</h2>
              <p>
                <strong>"De Hussel App"</strong> (hierna: "de App") is een webapplicatie ontwikkeld door 
                Ayemate (hierna: "wij", "ons" of "onze") voor het organiseren van hussel-, toss- en 
                racket-trek-avonden bij tennis- en padelclubs.
              </p>
              <p>
                <strong>"Gebruiker"</strong>: iedere natuurlijke persoon die een account aanmaakt om deel 
                te nemen aan sessies via de App.
              </p>
              <p>
                <strong>"Club"</strong>: iedere tennisvereniging, padelclub of vergelijkbare sportorganisatie 
                die de App gebruikt voor het organiseren van sessies.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Toepasselijkheid</h2>
              <p>
                Deze algemene voorwaarden zijn van toepassing op ieder gebruik van de App en alle 
                overeenkomsten die met ons worden aangegaan. Door een account aan te maken of de App 
                te gebruiken, gaat u akkoord met deze voorwaarden.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Accountregistratie</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>U dient minimaal 16 jaar oud te zijn om een account aan te maken.</li>
                <li>U bent verantwoordelijk voor het verstrekken van correcte en actuele gegevens.</li>
                <li>U bent verantwoordelijk voor het geheim houden van uw inloggegevens.</li>
                <li>U bent verantwoordelijk voor alle activiteiten die onder uw account plaatsvinden.</li>
                <li>U dient actief lid te zijn van de tennisclub die u selecteert bij registratie.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Gebruik van de App</h2>
              <p>De App is bedoeld voor:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Het aanmelden voor hussel-, toss- en racket-trek-sessies bij uw club.</li>
                <li>Het bekijken van speelindelingen en baantoewijzingen.</li>
                <li>Het ontvangen van notificaties over sessies.</li>
              </ul>
              <p className="mt-3">Het is niet toegestaan om:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>De App te gebruiken voor illegale of ongeoorloofde doeleinden.</li>
                <li>Valse of misleidende informatie te verstrekken.</li>
                <li>De veiligheid of integriteit van de App te ondermijnen.</li>
                <li>Persoonsgegevens van andere gebruikers te verzamelen of misbruiken.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Abonnementen en Betaling (voor Clubs)</h2>
              <p>
                Clubs die de App willen gebruiken dienen een maandelijks abonnement af te sluiten. 
                De tarieven zijn afhankelijk van de clubgrootte, gemeten naar het totaal aantal 
                <strong> actieve clubleden</strong> dat bij de club staat ingeschreven (conform 
                KNLTB-registratie). Dit betreft niet het aantal gebruikers dat de App gebruikt.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Abonnementen worden maandelijks gefactureerd via automatische incasso.</li>
                <li>Prijzen zijn exclusief BTW tenzij anders vermeld.</li>
                <li>Bij niet-betaling kan de toegang tot de App worden opgeschort.</li>
                <li>Opzegging is mogelijk met ingang van de volgende betaalperiode.</li>
                <li>
                  De clubgrootte wordt door ons geverifieerd bij de KNLTB. Indien het werkelijke 
                  ledenaantal afwijkt van het gekozen pakket, behouden wij ons het recht voor het 
                  abonnement aan te passen naar het juiste pakket.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Intellectueel Eigendom</h2>
              <p>
                Alle intellectuele eigendomsrechten op de App, inclusief maar niet beperkt tot 
                software, teksten, afbeeldingen en logo's, berusten bij Ayemate. Niets uit deze 
                App mag worden verveelvoudigd zonder voorafgaande schriftelijke toestemming.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Privacy</h2>
              <p>
                Wij verwerken persoonsgegevens in overeenstemming met de Algemene Verordening 
                Gegevensbescherming (AVG). Zie ons <a href="/privacy" className="text-primary hover:underline">Privacybeleid</a> voor 
                meer informatie over hoe wij met uw gegevens omgaan.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Aansprakelijkheid</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Wij zijn niet aansprakelijk voor schade als gevolg van onjuiste of onvolledige informatie in de App.</li>
                <li>Wij zijn niet aansprakelijk voor blessures of incidenten tijdens het spelen.</li>
                <li>Wij garanderen geen ononderbroken of foutloze werking van de App.</li>
                <li>Onze aansprakelijkheid is beperkt tot het bedrag dat in het betreffende geval door onze verzekeraar wordt uitgekeerd.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Beëindiging</h2>
              <p>
                Wij behouden ons het recht voor om accounts te beëindigen of toegang te weigeren 
                indien deze voorwaarden worden geschonden. U kunt uw account op elk moment 
                verwijderen via de accountinstellingen.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Wijzigingen</h2>
              <p>
                Wij behouden ons het recht voor om deze voorwaarden te wijzigen. Wijzigingen 
                worden via de App gecommuniceerd. Voortgezet gebruik na wijzigingen impliceert 
                acceptatie van de nieuwe voorwaarden.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">11. Gegevensbeveiliging</h2>
              <p>
                De veiligheid van uw gegevens is voor ons van het grootste belang. Wij treffen 
                uitgebreide technische en organisatorische maatregelen om uw persoonsgegevens te 
                beschermen tegen ongeautoriseerde toegang, verlies of misbruik.
              </p>
              <h3 className="text-lg font-medium mt-4 mb-2">Beveiligingsstandaarden</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Versleuteling in transit en at rest:</strong> Alle communicatie met de App verloopt 
                  via HTTPS/TLS-versleuteling. Gevoelige gegevens worden versleuteld opgeslagen 
                  met AES-256-encryptie.
                </li>
                <li>
                  <strong>Row Level Security (RLS):</strong> De database maakt gebruik van Row Level Security, 
                  waardoor gebruikers uitsluitend toegang hebben tot gegevens die voor hen bestemd zijn. 
                  Dit voorkomt ongeautoriseerde toegang tot gegevens van andere clubs of gebruikers.
                </li>
                <li>
                  <strong>Multi-tenant isolatie:</strong> Data van elke club is strikt gescheiden. 
                  Club-beheerders kunnen alleen gegevens van hun eigen club en leden inzien. 
                  Er is geen mogelijkheid om gegevens van andere clubs te benaderen.
                </li>
                <li>
                  <strong>Tweefactorauthenticatie (2FA):</strong> Beheerders kunnen tweefactorauthenticatie 
                  inschakelen voor extra beveiliging van hun account.
                </li>
                <li>
                  <strong>Regelmatige beveiligingsaudits:</strong> De applicatie wordt voorafgaand aan elke 
                  publicatie onderworpen aan een uitgebreide beveiligingsscan. Deze scan controleert op 
                  kwetsbaarheden conform de OWASP Top 10, waaronder SQL-injectie, Cross-Site Scripting (XSS), 
                  onveilige authenticatie en autorisatie, en blootstelling van gevoelige gegevens.
                </li>
                <li>
                  <strong>Veilige wachtwoordopslag:</strong> Wachtwoorden worden gehasht opgeslagen met 
                  industriestandaard algoritmen. Tijdelijke wachtwoorden in het registratieproces worden 
                  versleuteld met AES-GCM.
                </li>
                <li>
                  <strong>Minimale dataverzameling:</strong> Wij verzamelen alleen gegevens die strikt 
                  noodzakelijk zijn voor het functioneren van de dienst, conform het principe van 
                  dataminimalisatie uit de AVG.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">12. Account Verwijdering en Gegevensverwijdering</h2>
              <p>
                Gebruikers hebben te allen tijde het recht om hun account te verwijderen via de 
                Accountinstellingen in de App. Bij het verwijderen van uw account gebeurt het volgende:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Uw volledige profiel en alle bijbehorende persoonsgegevens worden permanent verwijderd uit onze systemen.</li>
                <li>Uw sessie-aanmeldingen en speelgeschiedenis worden definitief verwijderd.</li>
                <li>Eventuele profielfoto's worden verwijderd uit de opslag.</li>
                <li>De verwijdering is onomkeerbaar — er kan geen data worden hersteld na verwijdering.</li>
              </ul>
              <p className="mt-3">
                Factuurgegevens van clubs worden, in overeenstemming met fiscale wetgeving, 7 jaar 
                bewaard na het einde van het abonnement, ook als het account wordt verwijderd.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">13. Toepasselijk Recht</h2>
              <p>
                Op deze voorwaarden is Nederlands recht van toepassing. Geschillen worden 
                voorgelegd aan de bevoegde rechter in Nederland.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">14. Contact</h2>
              <p>
                Voor vragen over deze voorwaarden kunt u contact opnemen via:
              </p>
              <p className="mt-2">
                Ayemate<br />
                E-mail: <a href="mailto:info@ayemate.nl" className="text-primary hover:underline">info@ayemate.nl</a><br />
                Website: <a href="https://ayemate.nl" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">ayemate.nl</a>
              </p>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TermsOfService;
